// Types
import { useMemo } from 'react';
import castArray from 'lodash/castArray';
import compact from 'lodash/compact';
// Utils
import uniq from 'lodash/uniq';
import type { THistory } from '@timeedit/types/lib/types';
import HistoryDetail from './HistoryDetail';
import { useUserIdentity } from './hooks/useUserIdentity';
import './historyTime.scss';

interface Props {
  history?: THistory | THistory[];
  createdAt?: number;
  createdBy?: string;
  isSAML2?: boolean;
}

const HistoryTime = ({ isSAML2 = false, ...props }: Props) => {
  const history = useMemo(
    () => castArray(props.history ?? []),
    [props?.history]
  );

  const allIds = useMemo(
    () =>
      history
        ? uniq(
            compact(history.map((h) => h.modifiedBy).concat(props.createdBy))
          )
        : [],
    [history, props.createdBy]
  );

  const { names, isLoading } = useUserIdentity(allIds);

  const modified = useMemo(
    () =>
      history.map((h) => ({
        time: h.modified,
        label: (isLoading ? '...' : names[h.modifiedBy]) ?? 'N/A',
      })),
    [history, isLoading, names]
  );

  const created = useMemo(() => {
    if (!props.createdAt) return null;

    const name = (isLoading ? '...' : names[props.createdBy]) ?? 'N/A';
    const label = isSAML2 && name === 'SYSTEM' ? 'SSO' : name;

    return { time: props.createdAt, label };
  }, [props.createdAt, props.createdBy, isSAML2, names]);

  return (
    <div className="history-wrapper">
      {modified.length > 0 && (
        <HistoryDetail label="Last modified" history={modified} />
      )}
      {created != null && <HistoryDetail label="Created" history={created} />}
    </div>
  );
};

export default HistoryTime;
